var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LocalStorageCache } from '../utils/Cache';
import { uniq } from '../utils/uniq';
import { noop } from '../utils/noop';
import { getVisitorId } from '../utils/visitorId';
import { getSearchResults } from '../services/getSearchResults';
import { getSports } from '../services/getSports';
import { getPopularQueries } from '../services/getPopularQueries';
import { getConfig } from '../config';
import { SearchModule } from '../SearchModule';
// Load styles
import '../styles/main.scss';
// Load module configuration
const config = getConfig();
export function createSearchModule(options) {
    var _a, _b;
    // Extract and compute options
    const cachePrefix = (_a = options.cachePrefix) !== null && _a !== void 0 ? _a : 'whSearch';
    const maxPopularQueries = options.maxPopularQueries || 0;
    const maxPreviousQueries = options.maxPreviousQueries || 0;
    const minQueryLength = Math.max(options.minQueryLength || 0, 1);
    const debounceQuery = (_b = options.debounceQuery) !== null && _b !== void 0 ? _b : 300;
    const translations = options.translations || {};
    const locale = options.locale || config.locale;
    const apiUrl = options.apiUrl || config.searchApiUrl;
    const types = options.types || config.supportedTypes;
    const supportSportFilter = options.supportSportFilter || false;
    const featuredSports = options.featuredSports || [];
    // Validate options
    if (!apiUrl) {
        throw new Error('You need to provide Search API URL for the Search Module.');
    }
    // Build namespaced cache for the module
    const cache = new LocalStorageCache(cachePrefix);
    // Compute previous queries helpers
    const getPreviousQueries = maxPreviousQueries > 0
        ? () => (cache.get('previousQueries') || []).slice(0, maxPreviousQueries)
        : () => [];
    const setPreviousQueries = maxPreviousQueries > 0 ? (queries) => cache.set('previousQueries', queries) : noop;
    const recordPreviousQuery = (query) => {
        const previousQueries = uniq([query, ...getPreviousQueries()]).slice(0, maxPreviousQueries);
        setPreviousQueries(previousQueries);
        searchModule.setPreviousQueries(previousQueries);
    };
    // Instantiate the module
    const searchModule = new SearchModule({
        debounceQuery,
        translations,
        locale,
        apiUrl,
    });
    // Initialize previous queries
    searchModule.setPreviousQueries(getPreviousQueries());
    // Load the sports asynchronously
    if (supportSportFilter) {
        (() => __awaiter(this, void 0, void 0, function* () {
            const sports = yield getSports({
                apiUrl,
                featuredSports,
                locale,
            });
            searchModule.setSports(sports);
        }))();
    }
    // Load the popular queries asynchronously
    if (maxPopularQueries > 0) {
        (() => __awaiter(this, void 0, void 0, function* () {
            const popularQueries = yield getPopularQueries({
                limit: maxPopularQueries,
                apiUrl,
                locale,
            });
            searchModule.setPopularQueries(popularQueries.slice(0, maxPopularQueries));
        }))();
    }
    // Add helper classes for body when activated
    searchModule.on('open', () => { var _a; return (_a = document.body.classList) === null || _a === void 0 ? void 0 : _a.add('WhSearch--modal-open'); });
    searchModule.on('close', () => { var _a; return (_a = document.body.classList) === null || _a === void 0 ? void 0 : _a.remove('WhSearch--modal-open'); });
    // Support removing previous queries
    searchModule.on('removePreviousQuery', (query) => {
        setPreviousQueries(getPreviousQueries().filter((item) => item !== query));
        searchModule.setPreviousQueries(getPreviousQueries());
    });
    // Show loader during search
    searchModule.on('queryImmediate', ({ query }) => {
        if (query.length < minQueryLength) {
            searchModule.setLoading(false);
            searchModule.stopQueryPropagation();
            searchModule.setEntries([]);
        }
        else {
            searchModule.setLoading(true);
        }
    });
    // Support search
    let lastAbortController = null;
    searchModule.on('query', ({ query, sportId }) => __awaiter(this, void 0, void 0, function* () {
        // Stop previous search query
        lastAbortController === null || lastAbortController === void 0 ? void 0 : lastAbortController.abort();
        lastAbortController = new AbortController();
        const abortSignal = lastAbortController.signal;
        const userToken = getVisitorId();
        try {
            // Find search results
            const { items, categories, queryIDs } = yield getSearchResults({
                signal: abortSignal,
                query,
                sportId,
                locale,
                apiUrl,
                types,
                userToken,
            });
            // Handle race condition
            if (searchModule.getQuery() !== query || searchModule.getSportId() !== sportId) {
                return;
            }
            // Save query
            if (items.length > 0 && maxPreviousQueries > 0) {
                recordPreviousQuery(query);
            }
            //Analytics search results emitter
            yield searchModule.emit('results', { query, items, categories });
            // Show the results in the UI
            searchModule.setEntries(items, categories, queryIDs, true);
        }
        catch (error) {
            console.error('There was some error during search', error);
        }
        finally {
            if (!abortSignal.aborted) {
                searchModule.setLoading(false);
            }
        }
    }));
    return searchModule;
}
export function loadDependencies() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:
    if (!window.regeneratorRuntime) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore:
        return import('regenerator-runtime/runtime').then(() => null);
    }
    return Promise.resolve();
}
// Silent HMR
if (module.hot) {
    module.hot.accept();
}
//# sourceMappingURL=module.js.map